import type { FileType } from '@prisma/client';
import type { DocumentHubRow } from '@schema/document.schema';
import { generateClientImageUrl } from '@utils/browserImageHandling';
import { convertToSize } from '@utils/stringHelpers';
import { trpc } from '@utils/trpc';
import 'ag-grid-enterprise';
import type {
	ColDef,
	ICellRendererParams,
	RowClickedEvent,
	RowDataUpdatedEvent,
	RowSelectionOptions,
	SelectionChangedEvent,
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import Badge from '~/components/utils/Badge';
import { Button, RequestButton } from '~/components/utils/buttons';
import { FAIcon } from '~/components/utils/FAIcons';
import { dateFilterParams, dateFormatter } from '~/components/utils/grid/gridFormatters';
import { agGridTheme, setLicenseKey } from '~/components/utils/grid/gridUtilities';
import { ModalDialog } from '~/components/utils/ModalDialog';
import { FolderBreadcrumbsWithoutRouter } from '../jobs/attachments/FolderBreadcrumbs';

setLicenseKey();

const previewEdits = { resize: { fit: 'cover' as const, height: 100, width: 100 } };
const iconOrPreviewFormatter = (params: ICellRendererParams<DocumentHubRow>) => {
	if (!params.data) return '';
	if (params.data.type === 'folder') {
		return <FAIcon className="text-3xl" icon="folder" />;
	}
	return params.data.isImage ? (
		<img src={generateClientImageUrl(params.data, previewEdits)} alt={params.data.name} />
	) : (
		<FAIcon className="text-3xl" icon={params.data.icon} />
	);
};

const sizeFormatter = (params: ICellRendererParams<DocumentHubRow>) => {
	if (!params.data) return '';
	if (params.data.type === 'folder') {
		return `${params.data.documentCount} documents`;
	}
	return convertToSize(params.data.size);
};

const colDefs: ColDef<DocumentHubRow>[] = [
	{ cellRenderer: iconOrPreviewFormatter, suppressHeaderFilterButton: true, width: 140 },
	{ field: 'name', headerName: 'Name', filter: 'agTextColumnFilter', width: 250 },
	{
		field: 'createdAt',
		headerName: 'Created',
		filter: 'agDateColumnFilter',
		filterParams: dateFilterParams,
		valueFormatter: dateFormatter,
		width: 125,
	},
	{ cellRenderer: sizeFormatter, headerName: 'Size', suppressHeaderFilterButton: true, width: 125 },
];

export type ItemsSelectedHandler = (data: { documentIds: number[]; folderIds: number[] }) => Promise<unknown> | void;

type Props = {
	isOpen: boolean;
	onItemsSelected: ItemsSelectedHandler;
	setIsOpen: (isOpen: boolean) => void;
	fileTypeFilter?: FileType[];
	zIndex?: string;
};

type ContentsProps = Props & {
	isAttaching: boolean;
	setIsAttaching: (isAttaching: boolean) => void;
};

function Contents({ isAttaching, isOpen, onItemsSelected, setIsAttaching, setIsOpen, fileTypeFilter }: ContentsProps) {
	const gridRef = useRef<AgGridReact>(null);
	const [folderId, setFolderId] = useState<number | null>(null);
	const [selectedIdsByFolderId, setSelectedIdsByFolderId] = useState<{ [folderId: number]: Set<string> }>({});
	const { data: allFolders, isLoading: allFoldersAreLoading } = trpc.folders.allFolders.useQuery({ jobId: null }, { enabled: isOpen });
	const { data: documentHubRows, isLoading: documentHubRowsAreLoading } = trpc.documents.getSingleLevelDocumentHubRows.useQuery(
		{
			folderId,
			fileTypeFilter,
		},
		{ enabled: isOpen }
	);
	const selectedRowIds = useMemo(
		() => Object.values(selectedIdsByFolderId).reduce((acc, val) => [...acc, ...Array.from(val.values())], [] as string[]),
		[selectedIdsByFolderId]
	);
	const selectedEntityIds = useMemo(() => {
		const ids: Parameters<ItemsSelectedHandler>[0] = { documentIds: [], folderIds: [] };
		for (const rowId of selectedRowIds) {
			const [type, id] = rowId.split('-');
			ids[type === 'folder' ? 'folderIds' : 'documentIds'].push(Number(id));
		}
		return ids;
	}, [selectedRowIds]);
	const defaultColDef = useMemo(() => ({ filter: true, resizable: true }), []);
	const rowSelection = useMemo<RowSelectionOptions<DocumentHubRow, any>>(() => {
		return {
			mode: 'multiRow',
		};
	}, []);

	const getRowId = (row: DocumentHubRow) => `${row.type}-${row.id}`;
	const onRowClicked = (clickedEvent: RowClickedEvent<DocumentHubRow>) => {
		const target: Element | null | undefined = clickedEvent.event?.target as Element | null | undefined;
		if (!target || target.classList.contains('ag-checkbox-input')) return;
		if (clickedEvent.data?.type === 'folder' && !selectedIdsByFolderId[folderId ?? 0]?.has(getRowId(clickedEvent.data))) {
			setFolderId(clickedEvent.data.id);
		}
	};
	const onRowDataUpdated = (event: RowDataUpdatedEvent<DocumentHubRow>) => {
		const rowIds: string[] = selectedIdsByFolderId[folderId ?? 0]?.values().toArray() ?? [];
		const nodes = rowIds.map((rowId) => event.api.getRowNode(rowId)).filter((rowNode) => !!rowNode);
		event.api.setNodesSelected({ nodes, newValue: true });
	};
	const onSelectionChanged = (event: SelectionChangedEvent<DocumentHubRow>) => {
		setSelectedIdsByFolderId((prev) => ({
			...prev,
			[folderId ?? 0]: new Set(event.selectedNodes?.map((node) => node.id!)),
		}));
	};
	const onClickAttach = async () => {
		setIsAttaching(true);
		try {
			await onItemsSelected(selectedEntityIds);
			setIsOpen(false);
		} finally {
			setIsAttaching(false);
		}
	};

	useEffect(() => {
		setFolderId(null);
		setSelectedIdsByFolderId({});
	}, [isOpen]);

	return (
		<>
			<FolderBreadcrumbsWithoutRouter
				currentFolderId={folderId}
				folders={allFolders ?? []}
				setCurrentFolderId={setFolderId}
				topLevelLabel="Document Hub"
			/>
			<div className="ag-theme-alpine mt-2" style={{ height: 'calc(100vh - 250px)', width: '100%' }}>
				<AgGridReact<DocumentHubRow>
					theme={agGridTheme}
					columnDefs={colDefs}
					defaultColDef={defaultColDef}
					getRowHeight={(row) => (row.data?.type === 'document' && row.data?.isImage ? 100 : 40)}
					getRowId={(row) => getRowId(row.data)}
					// Without this AgGrid sometimes throws errors about re-rendering locks
					key={folderId ?? 0}
					loading={documentHubRowsAreLoading || allFoldersAreLoading}
					loadingOverlayComponent="agLoadingOverlay"
					loadingOverlayComponentParams={{ loadingMessage: 'Fetching data...' }}
					onRowDataUpdated={onRowDataUpdated}
					onRowClicked={onRowClicked}
					onSelectionChanged={onSelectionChanged}
					ref={gridRef}
					rowData={documentHubRows ?? []}
					rowSelection={rowSelection}
				/>
			</div>
			<div className="flex justify-between items-center border-t border-gray-200 mt-4 pt-2">
				<div>
					{selectedRowIds.length > 0 &&
						`${[
							selectedEntityIds.documentIds.length > 0 ? `${selectedEntityIds.documentIds.length} document(s)` : '',
							selectedEntityIds.folderIds.length > 0 ? `${selectedEntityIds.folderIds.length} folder(s)` : '',
						]
							.filter(Boolean)
							.join(', ')} selected`}
				</div>
				<div className="flex space-x-2">
					<Button className="text-gray-700 hover:bg-gray-200" hasBorder onClick={() => setIsOpen(false)}>
						Cancel
					</Button>
					<RequestButton
						className="bg-primary hover:bg-primary-dark text-white disabled:bg-gray-200 disabled:cursor-not-allowed px-4 py-2 rounded-md"
						disabled={selectedRowIds.length === 0}
						isLoading={isAttaching}
						onClick={onClickAttach}
					>
						<FAIcon icon="arrow-up-from-bracket" prefix="fal" className="mr-2" />
						Add
					</RequestButton>
				</div>
			</div>
		</>
	);
}

export default function SelectFromDocumentHubModal({ isOpen, onItemsSelected, setIsOpen, fileTypeFilter, zIndex }: Props) {
	const [isAttaching, setIsAttaching] = useState(false);

	return (
		<ModalDialog dialogOpen={isOpen} closeFn={() => setIsOpen(false)} maxW="max-w-3xl" zIndex={zIndex}>
			<fieldset disabled={isAttaching} className="disabled:cursor-not-allowed disabled:opacity-50">
				<div className="flex justify-between items-center border-b border-gray-200 mb-4 pb-2">
					<h1 className="text-xl font-bold">Select from Document Hub</h1>
					{fileTypeFilter?.length && (
						<Badge size="sm" colors="bg-orange-100">
							<span className="text-orange-800">{`Only showing ${fileTypeFilter.join(', ')} file types`}</span>
						</Badge>
					)}
				</div>
				<Contents
					isAttaching={isAttaching}
					isOpen={isOpen}
					onItemsSelected={onItemsSelected}
					setIsAttaching={setIsAttaching}
					setIsOpen={setIsOpen}
					fileTypeFilter={fileTypeFilter}
				/>
			</fieldset>
		</ModalDialog>
	);
}
